import { EmergenceIcon } from '@/components/icons/emergence-icon'
import { EyeLockIcon } from '@/components/icons/eye-lock-icon'
import { EyeOffIcon } from '@/components/icons/eye-off-icon'
import { LockOpenIcon } from '@/components/icons/lock-open-icon'
import { LockedIcon } from '@/components/icons/locked-icon'
import { Button } from '@/components/ui/button'
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card'
import { Markdown } from '@/components/ui/markdown'
import { Tooltip, TooltipContent, TooltipPortal, TooltipTrigger } from '@/components/ui/tooltip'
import {
  FrameworkCitation,
  SensitivityTagItem,
  SensitivityTagValue,
  TagItem,
} from '@/hooks/api/use-table-details-query'
import { cn } from '@/lib/utils'
import { X } from 'lucide-react'
import { TagCategory } from '../constants'
import { getTagColorByCategory } from '../utils'

interface NameButtonProps {
  name: string
  onClick: () => void
  disabled?: boolean
  title?: string
  className?: string
  enableTooltipForDisabled?: boolean
}

export const NameButton = ({
  name,
  onClick,
  disabled = false,
  title,
  className,
  enableTooltipForDisabled,
}: NameButtonProps) => {
  const trigger = (
    <Button
      onClick={onClick}
      disabled={disabled}
      unstyled
      className={cn(
        'cursor-pointer px-0 text-left font-semibold normal-case hover:text-mango-300',
        disabled && 'cursor-auto hover:text-grey-300',
        className,
      )}
      title={disabled ? undefined : title}
    >
      {name || '-'}
    </Button>
  )
  if (disabled && enableTooltipForDisabled) {
    return (
      <Tooltip delayDuration={100}>
        <TooltipTrigger asChild>{trigger}</TooltipTrigger>
        <TooltipPortal>
          <TooltipContent size='sm' align='start' side='bottom' className='w-[240px]'>
            {title}
          </TooltipContent>
        </TooltipPortal>
      </Tooltip>
    )
  }
  return trigger
}

interface DescriptionCellProps {
  data: string | null
  className?: string
}

export const DescriptionCell = ({ data, className }: DescriptionCellProps) => {
  return <p className={cn('text-neutral-500', className)}>{data || '-'}</p>
}

interface OwnersCellProps {
  owners: string[]
  className?: string
}

export const OwnersCell = ({ owners, className }: OwnersCellProps) => {
  if (owners.length === 0) {
    return <div className={cn('text-sm', className)}>-</div>
  }

  return (
    <div className={cn('flex flex-wrap gap-1', className)}>
      {owners.slice(0, 2).map((owner) => (
        <div key={owner} className='flex items-center gap-2 text-sm'>
          {owner === 'Emergence AI' && <EmergenceIcon className='size-6' />}
          {owner || '-'}
        </div>
      ))}
      {owners.length > 2 && <div className='text-sm'>+{owners.length - 2} more</div>}
    </div>
  )
}

interface TagsCellProps {
  tags: TagItem[]
  category: TagCategory
  onTagDelete?: (tagToDelete: string) => void
  isEditingDisabled?: boolean
  className?: string
}

export const TagsCell = ({
  tags,
  category,
  onTagDelete,
  isEditingDisabled = true,
  className,
}: TagsCellProps) => {
  if (!tags || tags.length === 0) {
    return <div className={cn('text-sm', className)}>-</div>
  }

  const handleTagClick = (tag: string, event: React.MouseEvent) => {
    event.stopPropagation()
    if (!isEditingDisabled && onTagDelete) {
      onTagDelete(tag)
    }
  }

  return (
    <div className={cn('flex flex-wrap gap-1', className)}>
      {tags.map((tagItem: TagItem) => {
        const colors = getTagColorByCategory(tagItem.tag, category, isEditingDisabled)

        const tagElement = (
          <div
            className={`flex gap-1 whitespace-nowrap rounded-md px-3 py-2 text-xs font-medium transition-colors ${
              isEditingDisabled
                ? `${colors.bg} opacity-80`
                : `${colors.bg} cursor-pointer hover:opacity-90`
            }`}
          >
            <span>{tagItem.tag}</span>
            {!isEditingDisabled && onTagDelete && (
              <Button
                unstyled
                type='button'
                className={`size-4 shrink-0 p-0 hover:bg-transparent ${colors.iconText}`}
                onClick={(e) => {
                  e.stopPropagation()
                  handleTagClick(tagItem.tag, e)
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    e.stopPropagation()
                    handleTagClick(tagItem.tag, e as any)
                  }
                }}
                aria-label={`Remove ${tagItem.tag} tag`}
                title={`Remove tag`}
              >
                <X className='size-4' />
              </Button>
            )}
          </div>
        )

        return (
          <TagHoverCard key={tagItem.tag} tag={tagItem}>
            {tagElement}
          </TagHoverCard>
        )
      })}
    </div>
  )
}

// Reusable component for tag hover cards
const TagHoverCard = ({ tag, children }: { tag: TagItem; children: React.ReactNode }) => {
  const handleMouseEnter = (e: React.MouseEvent) => {
    e.stopPropagation()
  }

  const handleMouseLeave = (e: React.MouseEvent) => {
    e.stopPropagation()
  }

  return (
    <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <HoverCard openDelay={200} closeDelay={100}>
        <HoverCardTrigger asChild>{children}</HoverCardTrigger>
        <HoverCardContent className='w-80 rounded-lg border border-white bg-neutral-25'>
          <div className='max-h-80 space-y-3 overflow-y-auto'>
            <h4 className='border-b border-neutral-75 pb-2 text-sm font-medium'>{tag.tag}</h4>
            {/* Framework Citations Section */}
            {renderFrameworkCitations(tag.framework_citations || tag.frameworkCitations || [])}

            {/* Description Section */}
            <div className='space-y-2'>
              <Markdown className='text-sm leading-relaxed'>
                {tag.description || 'No description'}
              </Markdown>
            </div>
          </div>
        </HoverCardContent>
      </HoverCard>
    </div>
  )
}

interface SensitivityTagsCellProps {
  sensitivityTags: SensitivityTagItem[]
  className?: string
}

export const SensitivityTagsCell = ({ sensitivityTags, className }: SensitivityTagsCellProps) => {
  if (!sensitivityTags || sensitivityTags.length === 0) {
    return <div className={cn('text-sm', className)}>-</div>
  }

  // Since there's only one tag object in the array, get the first one
  const tagItem = sensitivityTags[0]

  const getSensitivityIcon = (tag: SensitivityTagValue) => {
    switch (tag) {
      case 'Public':
        return <LockOpenIcon className='size-6 text-neutral-200' />
      case 'Internal':
        return <EyeOffIcon className='size-7 text-neutral-200' />
      case 'Confidential':
        return <EyeLockIcon className='size-6 text-neutral-200' />
      case 'Restricted':
        return <LockedIcon className='size-6 text-neutral-200' />
    }
  }

  const tagElement = (
    <div
      className={cn(
        'flex w-fit cursor-pointer items-center gap-1 rounded px-1 py-2 text-sm hover:bg-neutral-50',
        className,
      )}
    >
      {getSensitivityIcon(tagItem.tag)}
      <span>{tagItem.tag}</span>
    </div>
  )

  // Use the reusable TagHoverCard component
  return <TagHoverCard tag={tagItem}>{tagElement}</TagHoverCard>
}

export const renderFrameworkCitations = (citations: FrameworkCitation[]) => {
  if (!citations || citations.length === 0) {
    return null
  }

  return (
    <div className='space-y-3'>
      {citations.map((citation, index) => (
        <div key={index} className='space-y-2'>
          <h6 className='text-xs font-medium'>{citation.framework}</h6>
          <div className='space-y-1'>
            {citation.references.map((ref, refIndex) => (
              <div key={refIndex} className='text-xs'>
                <div className='font-medium text-neutral-500'>{ref.reference}</div>
                <div className='text-neutral-500'>{ref.summary}</div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}
